import * as yup from 'yup'

export interface PasswordConfig {
  minLength?: number
  maxLength?: number
  required?: boolean
  requireUppercase?: boolean
  requireLowercase?: boolean
  requireNumbers?: boolean
  requireSpecialChars?: boolean
}

const DEFAULT_CONFIG: Required<PasswordConfig> = {
  minLength: 8,
  maxLength: 256,
  required: true,
  requireUppercase: false,
  requireLowercase: false,
  requireNumbers: false,
  requireSpecialChars: false,
}

export function createPasswordSchema(config: PasswordConfig = {}): yup.StringSchema {
  const { minLength, maxLength, required } = {
    ...DEFAULT_CONFIG,
    ...config,
  }

  let schema = yup.string()

  if (required) {
    schema = schema.required('Password is required')
  }

  schema = schema
    .min(minLength, `Password must be at least ${minLength} characters`)
    .max(maxLength, `Password must be at most ${maxLength} characters`)

  // Implement "3 of 4" character type requirement
  schema = schema.test(
    'complexity',
    'Password must contain at least 3 of the following: uppercase letter, lowercase letter, number, special character',
    (value) => {
      if (!value) return true // Let required validation handle empty values

      let score = 0

      // Check for uppercase letters
      if (/[A-Z]/.test(value)) score += 1

      // Check for lowercase letters
      if (/[a-z]/.test(value)) score += 1

      // Check for numbers
      if (/\d/.test(value)) score += 1

      if (/[ !"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/.test(value)) score += 1

      return score >= 3
    }
  )

  return schema
}

export function passwordWithConfirmationSchema(
  passwordFieldName: string = 'password',
  confirmFieldName: string = 'confirmPassword',
  config: PasswordConfig = {}
) {
  return yup.object().shape({
    [passwordFieldName]: createPasswordSchema(config),
    [confirmFieldName]: yup
      .string()
      .required('Please confirm your password')
      .oneOf([yup.ref(passwordFieldName)], 'Passwords must match'),
  })
}

export function createUserPasswordSchema() {
  return createPasswordSchema({ maxLength: 256 })
}

export function updatePasswordSchema() {
  return passwordWithConfirmationSchema('newPassword', 'confirmPassword', { maxLength: 256 })
}
